import { AlfredThinkingFeedback } from '@terminal/components/alfred-footer/alfred-thinking-feedback/AlfredThinkingFeedback';
import { TrackingMetadata } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatDeepAnalysis } from './chat-deep-analysis/ChatDeepAnalysis';
import { ChatDocumentSearch } from './chat-document-search/ChatDocumentSearch';
import { ChatQuickAnalysis } from './chat-quick-analysis/ChatQuickAnalysis';
import { ChatInputConfig } from './types';

interface ThinkingFeedbackState {
  conversationId: string;
  instanceKey: number;
}

export interface ChatInputProps {
  submitCallback?: (message?: string) => void;
  chatContext?: {
    documents?: string[];
    documentTypes?: string[];
    entityTags?: string[];
  };
  config?: ChatInputConfig;
  trackingMetadata?: TrackingMetadata;
}

export const ChatInput = ({
  submitCallback,
  chatContext,
  config,
  trackingMetadata,
}: ChatInputProps) => {
  useTranslation(['chat']);
  const { mode, navigateToConversation } = useChatNavigation();
  const isAlfredThinkingFeedbackEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4987-alfred-thinking-feedback')
  );
  const feedbackInstanceKeyRef = useRef(0);
  const [thinkingFeedback, setThinkingFeedback] =
    useState<ThinkingFeedbackState>();

  const handleSubmitSuccess = (conversationId: string) => {
    feedbackInstanceKeyRef.current += 1;
    setThinkingFeedback({
      conversationId,
      instanceKey: feedbackInstanceKeyRef.current,
    });
  };

  const handleThinkingFeedbackDismiss = (instanceKey: number) => {
    setThinkingFeedback(currentFeedback =>
      currentFeedback?.instanceKey === instanceKey ? undefined : currentFeedback
    );
  };

  const handleThinkingFeedbackNavigate = (conversationId: string) => {
    if (trackingMetadata?.source) {
      navigateToConversation(conversationId, {
        trackingSource: trackingMetadata.source,
      });
      return;
    }

    navigateToConversation(conversationId);
  };

  if (chatContext) {
    return (
      <ChatDocumentSearch
        config={config}
        submitCallback={submitCallback}
        chatContext={chatContext}
        trackingMetadata={trackingMetadata}
      />
    );
  }

  if (mode === 'quickAnalysis') {
    return (
      <ChatQuickAnalysis
        config={config}
        submitCallback={submitCallback}
        trackingMetadata={trackingMetadata}
      />
    );
  }

  return (
    <>
      {thinkingFeedback ? (
        <AlfredThinkingFeedback
          key={thinkingFeedback.instanceKey}
          conversationId={thinkingFeedback.conversationId}
          onNavigate={handleThinkingFeedbackNavigate}
          onDismiss={() =>
            handleThinkingFeedbackDismiss(thinkingFeedback.instanceKey)
          }
        />
      ) : null}
      <ChatDeepAnalysis
        config={config}
        submitCallback={submitCallback}
        onSubmitSuccess={
          isAlfredThinkingFeedbackEnabled ? handleSubmitSuccess : undefined
        }
        trackingMetadata={trackingMetadata}
      />
    </>
  );
};
